import React, { useState, useRef, useEffect } from "react";
import { MediaStage } from "@/core/types/mediaTypes";
import { MEDIA_STAGE_CONFIG, MEDIA_STAGE_ORDER } from "@/core/MediaItem/mediaStageConfig";
import { apiClient } from "@/services/apiClient";
import { useAppStore } from "@/core/store/useAppStore";
import { useQueryClient } from "@tanstack/react-query";

interface BulkStageChangeDropdownProps {
  selectedIds: number[];
  onComplete: () => void;
  disabled?: boolean;
}

const STAGES = MEDIA_STAGE_ORDER.map((stage) => ({
  value: stage,
  label: MEDIA_STAGE_CONFIG[stage].label,
  color: MEDIA_STAGE_CONFIG[stage].color,
}));

export const BulkStageChangeDropdown: React.FC<BulkStageChangeDropdownProps> = ({
  selectedIds,
  onComplete,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedClient = useAppStore((state) => state.selectedClient);
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleStageSelect = async (stage: MediaStage) => {
    if (!selectedClient?.id || selectedIds.length === 0) return;

    setIsLoading(true);
    try {
      await apiClient.bulkUpdateStage(selectedClient.id, selectedIds, stage);

      // Invalidate media queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["media"] });

      setIsOpen(false);
      onComplete();
    } catch (error) {
      console.error("Failed to update stages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Aggiornamento...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
            Cambia stato
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {STAGES.map((stage) => (
            <button
              key={stage.value}
              onClick={() => handleStageSelect(stage.value)}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 transition-colors"
            >
              <div className={`w-3 h-3 rounded-full ${stage.color}`} />
              {stage.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
